import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type SerieValues = {
  fund: number;
  id?: number;
  quota_amount: string;
  quota_class: number;
  quota_order: number;
  quota_quantity: number;
  quota_undetermined: boolean;
  quota_final_date: string;
  amortization_frequency: string;
  amortization_grace_period: number;
  amortization_grace_period_display: string;
  amortization_information: string;
  amortization_initial_date: string;
  balance: number;
  benchmark: string;
  deleted: boolean;
  deleted_by_cascade: boolean;
  emission: string;
  quota_class_display: string;
  quota_cvm_register_date: string;
  quota_initial_date: string;
  quota_offer_type: number;
  quota_offer_type_display: string;
  quota_order_display: string;
  quota_serie_amount: string;
  quota_status: string;
  quote_offer_final_date: string;
};

export type FundValues = {
  additional_information_about_amortization: string;
  administration_fee: string;
  agent_fee: string;
  amortization_calculated: number;
  amortization_frequency: number;
  amortization_days: number;
  amortization_grace_period: number;
  analytical_description_of_credit_rights: string;
  anbima_classification: number;
  anbima_classification_label: string;
  application_conversion: number;
  application_conversion_unit: number;
  application_sale_off: number;
  application_sale_off_unit: number;
  assembly_date: string;
  assignment_expectation_greater_than_400: boolean;
  benchmark: string;
  bookkeeping_fee: string;
  cbm_constitution_date: string;
  certifier: string;
  change_date: string;
  charge_type: number;
  check_fee: string;
  client_service: {
    profile: number;
    internal_account: boolean;
    internal_account_number: string;
    checking_account: boolean;
    checking_account_bank: string;
    checking_account_number: string;
    checking_account_number_digit: string;
    checking_account_agency: string;
    charge_account: boolean;
    charge_account_bank: string;
    charge_account_number: string;
    charge_account_contract_number: string;
    charge_account_agency: string;
    audit: string;
    custody: boolean;
    escrow_account: boolean;
    escrow_account_bank: string;
    cblc: boolean;
    cblc_contract_number: string;
    cetip: boolean;
    cetip_contract_number: string;
    selic: boolean;
    selic_contract_number: string;
    guaranteed_cblc: boolean;
    guaranteed_cblc_contract_number: string;
    guaranteed_bmf: boolean;
    guaranteed_bmf_contract_number: string;
    outsourced_company: boolean;
    outsourced_company_name: string;
    zeroing_process: string;
    zeroing_bank: string;
    zeroing_agency: string;
    zeroing_account: string;
  };
  committed_zeroing: boolean;
  company: null;
  condominium_type: number;
  condominium_type_display: string;
  consultancy_fee: number;
  contact_name: string;
  controlling_fee: string;
  created: string;
  credit_rights_will_be_granted: boolean;
  creditors_rights: string;
  custody_fee: string;
  cvm_classification: number;
  delivery_asset_quotas_issuance: boolean;
  distribution_fee: null;
  document: string;
  document_number: string;
  giin: string;
  duration_period: string;
  email: string;
  entry_tax: number;
  event: number;
  external_id: number;
  fantasy_name: string;
  financial_assets: number;
  first_passport_date: string;
  form_filled_by: string;
  fund_builder: string;
  fund_current_profile: number;
  fund_social_exercise: number;
  general_fee: null;
  hiring_certifier: boolean;
  id: number;
  implantation_fee: string;
  initial_quota: string;
  is_simplified: boolean;
  last_company_cellphone_ddi: string;
  last_company_cellphone_number: string;
  last_company_contact: string;
  last_company_document: string;
  last_company_email: string;
  last_company_name: string;
  last_company_telephone_ddi: string;
  last_company_telephone_number: string;
  management_fee: string;
  manager_email: null;
  manager_name: null;
  manager_phone: null;
  min_additional_application: string;
  min_initial_application: string;
  min_permanence: string;
  min_redemption: string;
  name: string;
  other_tax: boolean;
  other_tax_description: string;
  out_tax: number;
  performance_tax: number;
  quota_fair_value: string;
  quota_offer_type: number;
  quota_series: SerieValues[];
  quota_types: number;
  rescue_conversion: number;
  rescue_conversion_unit: number;
  rescue_sale_off: number;
  rescue_sale_off_unit: number;
  risk_classification: string;
  service_providers: any[];
  short_name: null;
  slug: string;
  structuring_fee: string;
  structuring_fee_date: string;
  target_audience: number;
  tax_classification_deadline: number;
  tax_classification_tax: number;
  tax_classification_type: number;
  telephone_ddi: string;
  telephone_number: string;
  total_fee: string;
  transfer: boolean;
  transfer_date: string;
  type: number;
  updated: string;
  zeroing_automatic: {
    account: number;
    agency: number;
    bank: number;
    id?: number;
    zeroing_fund: number;
  };
  zeroing_account: {
    agency: number;
    bank: number;
    cetip: boolean;
    account_number: number;
    email: string;
    favored: string;
    favored_account_number: number;
    favored_cnpj: string;
    fund_bank: number;
    fund_cnpj: string;
    fund_contact_name: string;
    ted: boolean;
  };
  zeroing_committed: boolean;
  zeroing_manager: {
    email: string;
    name: string;
    phone: string;
  };
  links: any[];
  zeroing_phone: string;
  zeroing_process: boolean;
  files: any[];
};

type FundTransfer = {
  transfer_date: string;
  assembly_date: string;
  last_company_document: string;
  last_company_name: string;
  last_company_contact: string;
  last_company_cellphone_number: string;
  last_company_cellphone_ddi: string;
  last_company_telephone_ddi: string;
  last_company_telephone_number: string;
  last_company_email: string;
};

type FundBasic = {
  updated: string;
  financial_assets: number;
  condominium_type_display: string;
  fund_social_exercise: number;
  target_audience: number;
  cvm_classification: number;
  cbm_constitution_date: string;
  duration_period: string;
  structuring_fee_date: string;
  fantasy_name: string;
  name: string;
  giin: string;
  document: string;
  document_number: string;
  id: number;
  can_sign: boolean;
  completed: boolean;
  anbima_classification: number;
  step: number;
  client_service: {
    profile: number;
  };
  fund_current_profile: number;
  current_department: UserApprovalQueueDepartment;
  queue: UserApprovalQueue;
  sign_quantity: number;
  type: number;
  order: number;
  department?: Department;
  signatures: UserApprovalSignature[];
  attempt: number;
};

type FundComplementary = {
  tax_classification_deadline: number;
  tax_classification_tax: number;
  risk_classification: string;
  credit_rights_will_be_granted: boolean;
  creditors_rights: string;
  analytical_description_of_credit_rights: string;
  assignment_expectation_greater_than_400: boolean;
  quota_fair_value: string;
};

type FundQuotas = {
  quota_types: number;
  initial_quota: string;
  min_initial_application: string;
  min_additional_application: string;
  min_redemption: string;
  min_permanence: string;
  application_conversion: number;
  application_conversion_unit: number;
  application_sale_off: number;
  application_sale_off_unit: number;
  rescue_conversion: number;
  rescue_conversion_unit: number;
  rescue_sale_off: number;
  rescue_sale_off_unit: number;
  quota_series: SerieValues[];
};

type FundTax = {
  administration_fee: string;
  custody_fee: string;
  controlling_fee: string;
  bookkeeping_fee: string;
  general_fee: number;
  structuring_fee: string;
  implantation_fee: string;
  management_fee: string;
  distribution_fee: number;
  check_fee: string;
  agent_fee: string;
  consultancy_fee: number;
  total_fee: string;
  performance_tax: number;
  entry_tax: number;
  out_tax: number;
  charge_type: number;
  other_tax_description: string;
};

type FundBanking = {
  zeroing_fund_data: {
    document: number;
    corporate_name: string;
    manager_name: string;
    manager_phone: string;
    manager_email: string;
    bank: string;
    agency: number;
    account: number;
  };
  zeroing_automatic: {
    account: number;
    agency: number;
    bank: number;
    id?: number;
    zeroing_fund: number;
    zeroing_fund_data: {
      document: number;
      corporate_name: string;

      bank: string;
      agency: number;
      account: number;
    };
  };
  manager_name: string;
  manager_phone: string;
  manager_email: string;
  checking_account_bank: string;
  checking_account_agency: string;
  checking_account_number: string;
  checking_account_number_digit: string;
  charge_account_bank: string;
  charge_account_agency: string;
  charge_account_number: string;
  charge_account_contract_number: string;
  escrow_account_bank: string;
  cblc_contract_number: string;
  cetip_contract_number: string;
  selic_contract_number: string;
  guaranteed_cblc_contract_number: string;
  guaranteed_bmf_contract_number: string;
  internal_account_number: string;
};

type FundSignatures = {
  department_approval: {
    agent: {
      name: string;
    };
    department: {
      name: string;
    };
    type: string;
  };
  register: {
    person: {
      full_name: string;
    };
    company: {
      corporate_name: string;
    };
    type_display: string;
  };
  restart: boolean;
  date: string;
  reason: string;
};

type FundGroup = {
  basic: FundBasic;
  transfer: FundTransfer;
  complementary: FundComplementary;
  quotas: FundQuotas;
  providers: any[];
  tax: FundTax;
  banking: FundBanking;
  signatures: FundSignatures;
  assignors: Register[];
  shareholders: Register[];
};

type FundForm = {
  basic: boolean;
  complementary: boolean;
  tax: boolean;
  banking: boolean;
  providers: boolean;
};

type FundFormKeys = keyof FundForm;

@Injectable({
  providedIn: 'root',
})
export class FundService {
  data: FundValues | null = null;
  fundData: FundGroup | null = null;
  fundList: SelectItem[] = [];
  signatures: UserApprovalSignature[] = [];

  canEdit = new EventEmitter();
  onChange = new EventEmitter();
  fetchData = new EventEmitter();

  choices: { [Key in any]: any } = {};

  fundFormValid: FundForm = {
    basic: false,
    complementary: false,
    tax: false,
    banking: false,
    providers: false,
  };

  formStatusChangeEvent = new EventEmitter();

  private constitutionDateSubject = new BehaviorSubject<string>(null);
  public constitutionDate$ = this.constitutionDateSubject.asObservable();

  constructor() {}

  setConstitutionDate(date: string) {
    this.constitutionDateSubject.next(date);
  }

  getConstitutionDate(): string {
    return this.constitutionDateSubject.value;
  }

  setFundFormData(data: Partial<FundValues>): void {
    this.data = {
      ...this.data,
      ...data,
    };

    this.onChange.emit();
  }

  getFundFormData() {
    return this.data;
  }

  setFundFormStatus(key: FundFormKeys, status: boolean): void {
    if (this.fundFormValid[key] !== status) {
      this.fundFormValid[key] = status;
      this.formStatusChangeEvent.emit();
    }
  }

  getFundFormStatus(key: FundFormKeys): any {
    return this.fundFormValid[key];
  }

  reset() {
    this.data = null;
    this.fundData = null;
    this.signatures = [];
    this.fundList = [];
    this.choices = {};
    this.fundFormValid = {
      basic: false,
      complementary: false,
      tax: false,
      banking: false,
      providers: false,
    };
    this.constitutionDateSubject.next(null);
  }
}
